import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { useSelector } from 'react-redux'

import MetricsSection from '../MetricsSection'
import MetricsSectionSkeleton from '../MetricsSection/Skeleton'

import { getPixelStatsService } from '../../../../../../modules/services/lineItems'
import { getSelfAccountService } from '../../../../../../modules/services/selfAccounts'
import { selectedPartnershipSelector } from '../../../../../../modules/selectors/partnerships'

import { CUSTOM_EVENTS } from '../../fields'
import { getFormattedPixelStats } from '../../../../../../features/hooks/useFetchPromotedObjectFieldsData'

const title = 'Custom Events Metrics'
const CustomEventsMetricsSection = ({ formik }) => {
  const { t } = useTranslation()

  const selectedPartnership = useSelector(selectedPartnershipSelector)
  const partnerAccountId = selectedPartnership.partner

  const { data: partnerData, isLoading: partnerDataLoading } = useQuery({
    queryKey: ['partnerAccount', partnerAccountId],
    queryFn: () => getSelfAccountService(partnerAccountId),
    enabled: !!partnerAccountId,
    retry: false
  })
  // if more than one, take the first
  const pixelId = partnerData?.datasets?.[0]?.external_id

  const {
    data: pixelStatsData,
    isLoading,
    error: pixelStatsError
  } = useQuery({
    queryKey: ['customEvents', pixelId],
    queryFn: () => getPixelStatsService(pixelId),
    enabled: !!pixelId,
    retry: false
  })
  const pixelStats = useMemo(() => pixelStatsData?.data || [], [pixelStatsData])

  // reuse Exclude list
  const formattedPixelStats = useMemo(() => {
    return getFormattedPixelStats(pixelStats).map(metric => ({
      ...metric,
      // naturally_crafted, NaturallyCrafted, become: Naturally Crafted
      label: metric.label
        // Replace underscores or hyphens with spaces
        .replace(/[_-]+/g, ' ')
        // Insert a space before capital letters (for camelCase or PascalCase)
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        // Capitalize first letter of each word
        .replace(/\b\w/g, char => char.toUpperCase())
        .trim()
    }))
  }, [pixelStats])

  const isDataLoading = isLoading || partnerDataLoading
  const isPixelStatsWasLoaded = (!isDataLoading && pixelStatsData) || !!pixelStatsError
  const noPixelStats = isPixelStatsWasLoaded && formattedPixelStats?.length === 0

  if (isDataLoading) {
    return <MetricsSectionSkeleton title={t(title)} />
  } else if (noPixelStats || !pixelId) {
    // hide a section completely if there are no options
    return null
  }

  return (
    <MetricsSection
      title={t(title)}
      metricsGroupName={CUSTOM_EVENTS}
      metrics={formattedPixelStats}
      formik={formik}
      isCustomMetric={true}
    />
  )
}

export default CustomEventsMetricsSection
